import {
  Datagrid,
  List,
  Create,
  Edit,
  SimpleForm,
  TextField,
  TextInput,
  ReferenceArrayInput,
  NumberInput,
  ArrayInput,
  SimpleFormIterator,
  SelectArrayInput,
  AutocompleteArrayInput,
  Show,
  SimpleShowLayout,
  NumberField,
  ChipField,
  SingleFieldList,
  ReferenceArrayField,
  ArrayField,
  usePermissions,
} from 'react-admin';
import { ArchivalToolbar } from '../components/ArchivalToolbar';
import { RequireCourses } from '../components/DependencyGate';
import {
  ArchivedSelect,
  CourseField,
  CourseIdSelect,
  CourseSelect,
  FullNameField,
  getFullName,
} from '../components/selects';
import { useCourseIds } from '../utils/useCourseIds';
import { hasRole, type Permissions } from '../types';

const StudentGroupListInner = () => {
  const course_ids = useCourseIds();
  const { permissions } = usePermissions<Permissions>();

  return (
    <List
      exporter={false}
      filters={[
        <ArchivedSelect key="archived" />,
        <CourseIdSelect key="course" alwaysOn />,
      ]}
      sort={{ field: 'name', order: 'ASC' }}
      perPage={25}
      filter={course_ids ? { course_id: course_ids } : undefined}
    >
      <Datagrid
        rowClick={hasRole(permissions, 'tenant_admin') ? 'edit' : 'show'}
      >
        <TextField label="Class Name" source="name" />
        <CourseField />
      </Datagrid>
    </List>
  );
};

const StudentGroupList = () => (
  <RequireCourses>
    <StudentGroupListInner />
  </RequireCourses>
);

const StudentGroupShow = () => (
  <Show>
    <SimpleShowLayout>
      <TextField source="name" />
      <NumberField
        label="Maximum # of Students per Flight"
        source="max_cohort_size"
      />
      <CourseField />
      <ArrayField label="Assignment Groupings" source="assignment_groupings">
        <SingleFieldList>
          <ReferenceArrayField
            label="Assignments"
            source="assignment_ids"
            reference="assignments"
          >
            <SingleFieldList>
              <ChipField source="name" />
            </SingleFieldList>
          </ReferenceArrayField>
        </SingleFieldList>
      </ArrayField>
      <ReferenceArrayField
        label="Students"
        source="student_ids"
        reference="students"
      >
        <SingleFieldList>
          <FullNameField />
        </SingleFieldList>
      </ReferenceArrayField>
    </SimpleShowLayout>
  </Show>
);

const StudentGroupCreate = () => (
  <Create>
    <StudentGroupForm />
  </Create>
);

const StudentGroupEdit = () => (
  <Edit mutationMode="pessimistic">
    <StudentGroupForm />
  </Edit>
);

const StudentGroupForm = () => (
  <SimpleForm toolbar={<ArchivalToolbar />}>
    <TextInput fullWidth label="Class Name" source="name" />
    <NumberInput
      fullWidth
      label="Maximum # of Students per Flight"
      source="max_cohort_size"
    />
    <CourseSelect />
    <ArrayInput label="Assignment Groupings" source="assignment_groupings">
      <SimpleFormIterator>
        <ReferenceArrayInput
          label="Assignments"
          perPage={100}
          source="assignment_ids"
          sort={{ field: 'name', order: 'ASC' }}
          reference="assignments"
        >
          <SelectArrayInput optionText="name" />
        </ReferenceArrayInput>
      </SimpleFormIterator>
    </ArrayInput>
    {/* R2 Task 3: ReferenceInput source="" inside an ArrayInput could never
        bind selections. Replaced with ReferenceArrayInput + chips, matching
        the shape the API stores (student_ids: string[]). */}
    <ReferenceArrayInput
      label="Students"
      perPage={100}
      source="student_ids"
      sort={{ field: 'last_name', order: 'ASC' }}
      reference="students"
    >
      <AutocompleteArrayInput
        fullWidth
        label="Students"
        optionText={getFullName}
      />
    </ReferenceArrayInput>
  </SimpleForm>
);

export const student_groups = {
  list: StudentGroupList,
  show: StudentGroupShow,
  create: StudentGroupCreate,
  edit: StudentGroupEdit,
};
